/**
 * Raggruppamento delle offerte per **tratta**, che è l'unità di scelta del viaggiatore.
 *
 * Non è il verticale: un A/R in treno sono due tratte (andata e ritorno), Goelett le cerca
 * come due one-way distinte e il viaggiatore deve scegliere un'offerta per ciascuna. Finché
 * la pagina teneva una scelta per `segmentType`, selezionare il ritorno deselezionava
 * l'andata e un A/R in treno non era prenotabile.
 *
 * La chiave arriva dal backend (`NormalizedOffer.legKey`, es. `rail:outbound`). I preventivi
 * generati prima della sua introduzione non l'hanno: si ricade sul `segmentType`, che è
 * esattamente la regola con cui erano stati costruiti, così un link già in circolazione
 * continua a funzionare come prima.
 */

/** Ordine in cui i verticali compaiono nel preventivo. */
export const SERVICE_ORDER = ['air', 'rail', 'hotel', 'car']

/**
 * Chiave della tratta di un'offerta.
 *
 * @param {Object} offer - Offerta normalizzata
 * @returns {string} `legKey` se presente, altrimenti il `segmentType`
 */
export function legKeyOf(offer) {
  return offer?.legKey || offer?.segmentType || ''
}

/**
 * Divide le offerte del preventivo in gruppi selezionabili, uno per tratta.
 *
 * L'ordine è quello di `SERVICE_ORDER` e, dentro un verticale, quello di apparizione delle
 * tratte: il backend accoda l'andata prima del ritorno, quindi il viaggiatore le vede
 * nell'ordine del viaggio senza che la pagina debba conoscere le direzioni.
 *
 * @param {Object} offersByService - `{ air: [], rail: [], hotel: [], car: [] }`
 * @returns {Array<{key: string, serviceKey: string, direction?: string, offers: Array}>}
 */
export function buildLegGroups(offersByService) {
  const groups = []

  for (const serviceKey of SERVICE_ORDER) {
    const offers = offersByService?.[serviceKey]
    if (!Array.isArray(offers) || !offers.length) continue

    const byLeg = new Map()
    for (const offer of offers) {
      const key = legKeyOf(offer)
      if (!byLeg.has(key)) {
        byLeg.set(key, {
          key,
          serviceKey,
          // La direzione serve solo a intitolare il gruppo; è valorizzata sul rail.
          direction: offer?.details?.direction,
          ordinal: legOrdinal(key),
          offers: []
        })
      }
      byLeg.get(key).offers.push(offer)
    }

    groups.push(...byLeg.values())
  }

  return groups
}

/**
 * Ordinale della tratta letto dalla chiave, se c'è.
 *
 * La chiave porta l'ordinale solo quando il verticale ha più di una tratta (`air:2`), e mai
 * per la direzione (`rail:outbound`): con un solo volo non c'è nessuna «tratta 1» da mostrare.
 *
 * @param {string} key - Chiave della tratta
 * @returns {number|undefined} Ordinale 1-based, oppure undefined
 */
export function legOrdinal(key) {
  const ordinal = Number(String(key).split(':')[1])
  return Number.isInteger(ordinal) ? ordinal : undefined
}

/**
 * Titolo del gruppo: il verticale, più l'ordinale della tratta e la direzione quando ci sono.
 *
 * Esempi: «Voli», «Voli · Tratta 2», «Treni · Andata».
 *
 * @param {Object} t - Dizionario tradotto
 * @param {{serviceKey: string, direction?: string, ordinal?: number}} group - Gruppo
 * @returns {string} Titolo leggibile
 */
export function legTitle(t, group) {
  const parts = [t?.services?.[group.serviceKey] || group.serviceKey]
  if (group.ordinal && t?.services?.leg) parts.push(t.services.leg(group.ordinal))
  if (group.direction && t?.offer?.[group.direction]) parts.push(t.offer[group.direction])
  return parts.join(' · ')
}
